import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const normalizeDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const buildClosestLabelResolver = (labels) => {
  const normalizedLabelDates = (labels || [])
    .map((label) => normalizeDate(label))
    .filter(Boolean)
    .map((date) => ({
      key: date,
      time: new Date(date).getTime(),
    }));

  if (!normalizedLabelDates.length) {
    return (date) => date;
  }

  return (date) => {
    const targetTime = new Date(date).getTime();
    if (Number.isNaN(targetTime)) return date;

    let closest = normalizedLabelDates[0];
    for (let index = 1; index < normalizedLabelDates.length; index += 1) {
      const candidate = normalizedLabelDates[index];
      if (Math.abs(candidate.time - targetTime) < Math.abs(closest.time - targetTime)) {
        closest = candidate;
      }
    }

    return closest.key;
  };
};

const getScaleMeta = (maxValue) => {
  if (maxValue >= 1_000_000_000) return { scale: 1_000_000_000, unit: "B" };
  if (maxValue >= 1_000_000) return { scale: 1_000_000, unit: "M" };
  if (maxValue >= 1_000) return { scale: 1_000, unit: "K" };
  return { scale: 1, unit: "" };
};

const formatCurrencyKOnly = (value) => {
  if (!Number.isFinite(value)) return "-";
  const sign = value < 0 ? "-" : "";
  const absValue = Math.abs(value);

  if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return `${sign}$${absValue.toFixed(2).replace(/\.?0+$/, "")}`;
};

const formatDateTickLabel = (rawLabel) => {
  if (!rawLabel) return "";
  const text = String(rawLabel);

  const matched = text.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})/);
  if (matched) {
    return `${matched[1].slice(2)}.${matched[2]}`;
  }

  const parsedDate = new Date(text);
  if (Number.isNaN(parsedDate.getTime())) return text;

  const year = String(parsedDate.getFullYear()).slice(2);
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  return `${year}.${month}`;
};

const buildLegendLabels = (chart) => (
  ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
    .filter((item) => !chart?.data?.datasets?.[item.datasetIndex]?.legendHidden)
);

const BAR_COLORS = {
  buy: "rgba(220, 53, 69, 0.8)",
  sell: "rgba(26, 29, 226, 0.8)",
  default: "rgba(255, 99, 132, 0.8)",
};

export default function StockBarChart({
  chartLabels,
  chartSelection,
  baseChartData = null,
  trades = [],
  chartMeta = {},
}) {
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const resolvedLabels = useMemo(() => (
    baseChartData?.labels || chartLabels || []
  ), [baseChartData, chartLabels]);
  const resolvedMeta = useMemo(() => {
    if (baseChartData) return chartMeta;
    return {
      categoryKey: chartSelection?.categoryKey,
      status: chartSelection?.status,
      selectedName: chartSelection?.selectedName,
      isInsider: chartSelection?.isInsider,
    };
  }, [baseChartData, chartMeta, chartSelection]);
  const useAllInsiderTrades = !baseChartData && Boolean(
    chartSelection?.isInsider && chartSelection?.useAllInsiderTrades
  );
  const resolvedTrades = useMemo(() => (
    baseChartData
      ? trades
      : (
        useAllInsiderTrades
          ? (chartSelection?.barTrades || [])
          : (chartSelection?.selectedTrades || [])
      )
  ), [baseChartData, trades, chartSelection, useAllInsiderTrades]);
  const isInsider = baseChartData
    ? resolvedMeta?.categoryKey === "INSIDER"
    : chartSelection?.isInsider;

  const barData = useMemo(() => {
    if (!resolvedTrades.length && !resolvedLabels.length) return null;

    const isSell = resolvedMeta?.status === "sell";
    const barColor = isSell ? BAR_COLORS.sell : BAR_COLORS.buy;
    const categoryName = resolvedMeta?.categoryKey === "FUND" ? "미국연기금" : "투자대가";
    const insiderLabel = useAllInsiderTrades
      ? `내부자 전체 ${isSell ? "매도" : "매수"} 금액`
      : `${resolvedMeta?.selectedName || "내부자"}의 ${isSell ? "매도" : "매수"} 금액`;
    const groupedValues = {};
    const resolveClosestLabelDate = buildClosestLabelResolver(resolvedLabels);

    resolvedTrades.forEach((trade) => {
      const date = normalizeDate(
        trade?.pubdate || trade?.reportdate || trade?.date || trade?.transactionend
      );
      if (!date) return;

      const rawValue = isInsider
        ? trade?.transactionamount ?? trade?.totvalue ?? trade?.numshare
        : trade?.totvalue ?? trade?.totalvalue ?? trade?.totValue ?? trade?.transactionamount;
      const value = Number(rawValue);
      if (!Number.isFinite(value) || value <= 0) return;

      const alignedDate = resolveClosestLabelDate(date);
      groupedValues[alignedDate] = (groupedValues[alignedDate] || 0) + value;
    });

    const labels = resolvedLabels.length > 0 ? resolvedLabels : Object.keys(groupedValues).sort((a, b) => new Date(a) - new Date(b));
    if (!labels.length) {
      return {
        data: {
          labels: ["데이터 없음"],
          datasets: [
            {
              type: "bar",
              label: isInsider
                ? insiderLabel
                : `${resolvedMeta?.selectedName || categoryName} 보유주식 평가액 추이`,
              data: [null],
              backgroundColor: isInsider ? barColor : BAR_COLORS.default,
              borderColor: isInsider ? barColor : BAR_COLORS.default,
              borderWidth: 0.6,
              barPercentage: 0.9,
              categoryPercentage: 1.0,
              barThickness: isMobile ? 10 : 20,
              minBarLength: 3,
              clip: false,
            },
          ],
        },
        scale: 1,
        unit: "",
      };
    }

    // Map ALL labels to values (0 if missing)
    const normalizedGroupedValues = {};
    Object.keys(groupedValues).forEach(k => {
      normalizedGroupedValues[normalizeDate(k)] = groupedValues[k];
    });

    const dataPoints = labels.map((label) => normalizedGroupedValues[normalizeDate(label)] || 0);

    const maxValue = Math.max(0, ...dataPoints.filter((value) => value !== null));
    const { scale, unit } = getScaleMeta(maxValue);
    const barBgColor = isInsider ? barColor : BAR_COLORS.default;
    const barBorderColor = isInsider ? barColor : BAR_COLORS.default;

    const label = isInsider
      ? insiderLabel
      : `${resolvedMeta?.selectedName || categoryName} 보유주식 평가액 추이`;

    return {
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label,
            data: dataPoints.map((value) => (value !== null ? value / scale : null)),
            backgroundColor: barBgColor,
            borderColor: barBorderColor,
            borderWidth: 0.6,
            barPercentage: 0.9,
            categoryPercentage: 1.0,
            barThickness: isMobile ? 4 : 8,
            minBarLength: 0,
            clip: false,
          },
        ],
      },
      scale,
      unit,
    };
  }, [resolvedLabels, resolvedTrades, resolvedMeta, isInsider, isMobile, useAllInsiderTrades]);

  const options = useMemo(() => {
    if (!barData) return {};
    const { scale, unit } = barData;

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { top: 0, left: 0, right: 0, bottom: 0 } },
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "center",
          labels: {
            color: chartColors.legendColor,
            boxWidth: 12,
            boxHeight: 12,
            padding: 22,
            generateLabels: buildLegendLabels,
          },
        },
        tooltip: {
          backgroundColor: isDarkMode ? "rgba(30, 40, 55, 0.95)" : "rgba(255, 255, 255, 0.95)",
          titleColor: isDarkMode ? "#fff" : "#333",
          bodyColor: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "#666",
          borderColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          borderWidth: 1,
          callbacks: {
            label: (context) => {
              const value = context.raw;
              if (value == null || value === 0) return "";
              return formatCurrencyKOnly(value * scale);
            },
          },
        },
      },
      scales: {
        x: {
          offset: false, // Align bars with ticks precisely
          ticks: {
            display: true,
            color: chartColors.tickColor,
            autoSkip: true,
            maxTicksLimit: isMobile ? 5 : 8,
            callback: function (value) {
              const label = this.getLabelForValue(value);
              return formatDateTickLabel(label);
            },
          },
          grid: { display: false, offset: false },
          border: { color: chartColors.borderColor },
        },
        y: {
          position: "left",
          beginAtZero: true,
          afterFit: (axis) => { axis.width = 60; },
          ticks: {
            color: chartColors.tickColor,
            callback: (value) => (!unit ? `$${value}` : `$${Math.round(value)}${unit}`),
            padding: 0
          },
          grid: { display: true, color: chartColors.gridColor },
          border: { color: chartColors.borderColor },
        },
        yRight: {
          position: "right",
          afterFit: (axis) => { axis.width = 60; },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        }
      },
    };
  }, [barData, chartColors, isDarkMode, isMobile]);

  if (!barData) return null;

  return (
    <div className="w-100 h-100">
      <Bar data={barData.data} options={options} />
    </div>
  );
}
